/**
 * The transcript panel's pure parts: finding a search in the cues, cutting a line into its
 * highlighted pieces, and naming and colouring a voice nobody named.
 */

/** One place a search was found: a cue, and the matched range of its text. */
export interface Occurrence {
  cue: number
  start: number
  end: number
}

/** A piece of a line, as the panel draws it. */
export interface Piece {
  text: string
  match: boolean
  current: boolean
}

/**
 * A text folded for search — lowercase, accents removed — with, for each folded character, the
 * index of the original character it came from, so a match is drawn on the text as written.
 */
export interface Folded {
  folded: string
  origin: number[]
}

function fold(text: string): Folded {
  let folded = ''
  const origin: number[] = []
  for (let index = 0; index < text.length; index++) {
    const char = text[index]!
    const plain = char.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
    for (const piece of plain) {
      folded += piece
      origin.push(index)
    }
  }
  return { folded, origin }
}

/**
 * The cues' texts folded for search, once per set of cues: a search runs on every keystroke, and
 * folding a three-hour session's lines each time would be most of its cost.
 */
export function foldTexts(texts: readonly string[]): Folded[] {
  return texts.map(fold)
}

/**
 * Every occurrence of `query` in the cues' folded texts, in reading order, ignoring case and
 * accents — « college » finds « collèges ». A blank query finds nothing.
 */
export function findOccurrences(texts: readonly Folded[], query: string): Occurrence[] {
  const needle = fold(query.trim()).folded
  if (!needle) return []
  const found: Occurrence[] = []
  texts.forEach(({ folded, origin }, cue) => {
    let from = 0
    for (;;) {
      const at = folded.indexOf(needle, from)
      if (at === -1) break
      const last = at + needle.length - 1
      found.push({ cue, start: origin[at]!, end: origin[last]! + 1 })
      from = at + needle.length
    }
  })
  return found
}

/**
 * A line cut into the pieces the panel draws: plain text, matches, and the match the search is on.
 * `ranges` are this line's occurrences, in order.
 */
export function pieces(text: string, ranges: readonly Occurrence[], current: Occurrence | null): Piece[] {
  const out: Piece[] = []
  let at = 0
  for (const range of ranges) {
    if (range.start > at) out.push({ text: text.slice(at, range.start), match: false, current: false })
    out.push({
      text: text.slice(range.start, range.end),
      match: true,
      current: current !== null && current.cue === range.cue && current.start === range.start,
    })
    at = range.end
  }
  if (at < text.length) out.push({ text: text.slice(at), match: false, current: false })
  return out
}

/**
 * The colours a voice nobody coloured is drawn in, by its rank — readable on the panel's dark and
 * light surfaces alike.
 */
export const SPEAKER_PALETTE = ['#a78bfa', '#38bdf8', '#f59e0b', '#34d399', '#f472b6', '#fb7185', '#60a5fa', '#a3e635']

/**
 * Each voice's rank, from 1, by its label: the order « Intervenant 1, 2… » follows, the same on
 * every screen since it depends on the labels alone.
 */
export function speakerRanks(labels: Iterable<string>): Map<string, number> {
  const sorted = [...new Set(labels)].sort()
  return new Map(sorted.map((label, index) => [label, index + 1]))
}

/** A voice's colour: its own, else the palette's at its rank. */
export function speakerColor(color: string | null | undefined, rank: number): string {
  return color || SPEAKER_PALETTE[(rank - 1) % SPEAKER_PALETTE.length]!
}
